import {
  DATA_LINK_ACTIONS,
  dataLinkDocumentsCountedAction,
  dataLinkDocumentSelectedAction,
  dataLinkDocumentsLoadedAction
} from '../../redux/actions/datalinks';
import { parse } from '../parse';
import { getDb } from '../../firebase';
import {
  collection,
  query,
  onSnapshot,
  doc,
  where,
  Timestamp,
  limit,
  getCountFromServer
} from 'firebase/firestore';
import { consoleLog } from '../../utils/Logger';

const _ = require('lodash');

/*
    generate the firestore path for a data link
  */
const getDataLinkPath = ({
  dataLink,
  documents,
  selections,
  user,
  local,
  dynamic,
  dataLinks,
  data,
  params
}) => {
  const segments = [];
  let invalidSegment = false;

  dataLink.pathsegments
    .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
    .forEach(segment => {
      if (segment.type === 'collection') {
        if (segment.collectionName.indexOf('{') > -1) {
          const collectionParsed = parse({
            markup: segment.collectionName,
            documents,
            selections,
            local,
            user,
            dataLinks
          });
          segments.push(collectionParsed);
        } else {
          segments.push(segment.collectionName);
        }
      } else if (segment.type === 'id') {
        if (segment.staticDocumentId.indexOf('{dynamic.') === 0 && dynamic) {
          segments.push(
            dynamic[
              segment.staticDocumentId.replace('{dynamic.', '').replace('}', '')
            ]
          );
        } else {
          segments.push(segment.staticDocumentId);
        }
      } else if (segment.type === 'datalink') {
        if (selections[segment.datalink]) {
          try {
            segments.push(selections[segment.datalink]?.documentId);
          } catch {
            /*
              If we tap an item in a list and then go to a details screen and that details screen has a list
              that is reliant on the selected id from the list tap, then that id might not yet be in 'selections'
            */
            invalidSegment = true;
          }
        } else {
          segments.push(segment.datalink);
        }
      } else if (segment.type === 'local') {
        if (
          segment.localFieldName.indexOf &&
          segment.localFieldName.indexOf('{') === -1
        ) {
          segments.push(
            _.get(local, segment.localFieldName.replace('local.', ''))
          );
        } else {
          segments.push(segment.localFieldName);
        }
      } else {
        // selector
        try {
          segments.push(
            selections[segment.selectorElementDataLinkId].documentId
          );
        } catch {
          /*
            If we tap an item in a list and then go to a details screen and that details screen has a list
            that is reliant on the selected id from the list tap, then that id might not yet be in 'selections'
          */
          invalidSegment = true;
        }
      }
    });

  if (invalidSegment) {
    return false;
  }

  let path = segments.join('/');
  // static document id path segments might have placeholders in them
  path = parse({
    markup: path,
    documents,
    selections,
    user,
    local,
    dataLinks,
    data,
    params
  });

  return path;
};

const getDataLinkFilters = ({
  dataLink,
  documents,
  selections,
  local,
  user,
  contentType,
  dataLinks,
  data,
  params
}) => {
  let filters =
    dataLink.type === 'user'
      ? dataLink.filters
      : dataLink.filters?.map(filter => {
          if (filter.value?.toString().indexOf('user.') > -1) {
            return {
              ...filter,
              value: filter.value
                .replace('user.id', user.id)
                .replace('user.email', user.email)
                .replace('user.userID', user.userID)
                .replace(/user\.firstName/, user.firstName)
                .replace(/user\.lastName/, user.lastName)
                .replace(/{/g, '')
                .replace(/}/g, '')
                .replace(/\$/g, '')
            };
          }

          return filter;
        });

  if (!filters) {
    return [];
  }

  filters = filters.filter(f => !f.joinWithDataLink);

  /* prevent filter failures on user filters immediately after the user has been created but has not had their id field set */
  /*filters = filters.map((filter) => {
    return {
      ...filter,
      value: filter.value.replace('{user.id}', '{user.userID}')
    };
  });*/

  return filters?.map(filter => {
    const firestoreFilter = { ...filter };
    let value = filter.value;
    if (filter.operator.toUpperCase() === 'IS_NOT_EMPTY') {
      value = '';
      firestoreFilter.operator = '!=';
    } else if (filter.operator.toUpperCase() === 'IS_EMPTY') {
      value = '';
      firestoreFilter.operator = '==';
    }

    if (value === '{date}') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      value = Timestamp.fromDate(now);
    } else if (value === '{datetime}') {
      value = Timestamp.fromDate(new Date());
    } else {
      value = parse({
        markup: value,
        documents,
        selections,
        user,
        local,
        dataLinks,
        data,
        params
      });
    }

    const contentTypeField = contentType?.fields?.find(
      f => f.name === firestoreFilter.field
    );

    return {
      field: firestoreFilter.field,
      operator: firestoreFilter.operator,
      value:
        contentTypeField?.type === 'string' && value?.toString
          ? value.toString()
          : value
    };
  });
};

/*
  inspects a data link and determines if it has dependencies on other data links
   this function can be used to determine whether to load the data link last after other data links have loaded
   
*/
const dataLinkHasDependencies = dataLink => {
  if (!dataLink) {
    return false;
  }

  if (dataLink.databaseId?.indexOf('{') > -1) {
    return true;
  }

  if (
    dataLink.pathsegments.some(s => s.type === 'datalink' || s.type === 'local')
  ) {
    return true;
  }

  if (dataLink.pathsegments.some(s => s.collectionName?.indexOf('{') > -1)) {
    return true;
  }

  if (dataLink.filters.some(s => s.value?.indexOf('{') > -1)) {
    return true;
  }

  return false;
};

/* load the data from firestore */
const loadDataLinkData = async ({
  dataLink,
  parameters,
  selections,
  user,
  firebaseUser,
  documents,
  dispatch,
  local,
  dynamic,
  contentType,
  dataLinks,
  autoSelection,
  data,
  params
}) => {
  consoleLog('loadDataLinkData', { dataLink });
  /* 
    we have not previously loaded the documents for this dataLink, 
    so create a snapshot listener, including any filters defined on the Data link
  */
  let queryParts = [];
  let q;
  let filters = [];

  if (dataLink.filters?.length > 0) {
    filters = getDataLinkFilters({
      dataLink,
      documents,
      selections,
      local,
      user,
      contentType,
      dataLinks,
      data,
      params
    });

    queryParts = filters.map(filter => {
      return where(filter.field, filter.operator, filter.value);
    });
  }

  const path = getDataLinkPath({
    parameters,
    dataLink,
    documents,
    selections,
    user,
    local,
    dynamic,
    dataLinks,
    data
  });

  if (path.indexOf('{') > -1) {
    /* path still contains placeholders that cannot yet be resolved */
    consoleLog(`dataLink ${dataLink.name} cannot be loaded yet. ${path}`);
    return [];
  }

  if (path === false) {
    /*
      If we tap an item in a list and then go to a details screen and that details screen has a list
      that is reliant on the selected id from the list tap, then that id might not yet be in 'selections'.
      We just exit this function, because a change to 'selections' in the store, when the id is there, will trigger
      the useDataSource hook useEffect to call loadDataLinkData again
    */
    return [];
  }

  let dataLinkDb;
  let databaseId = '(default)';

  if (dataLink.databaseId?.trim().length > 0) {
    databaseId = parse({
      markup: dataLink.databaseId,
      selections,
      local,
      user,
      document,
      documents,
      dataLinks
    });
    if (databaseId.indexOf('{') > -1) {
      return [];
    }
    dataLinkDb = getDb(databaseId);
    consoleLog(
      `using databaseId ${dataLinkDb._databaseId.database} for dataLink ${dataLink.name}`
    );
  } else {
    dataLinkDb = getDb();
    consoleLog(
      `using databaseId ${dataLinkDb._databaseId.database} for dataLink ${dataLink.name}`
    );
  }

  /* if parameters already exist for this data link and they have not changed, then don't reload it */
  if (parameters && parameters[dataLink.id]) {
    const dataLinkParams = parameters[dataLink.id];
    if (
      dataLinkParams.path === path &&
      dataLinkParams.databaseId === dataLinkDb._databaseId.database
    ) {
      if (
        dataLinkParams.filters.length === filters.length &&
        dataLinkParams.filters.every(f =>
          filters.find(
            filter =>
              filter.field === f.field &&
              filter.value === f.value &&
              filter.operator === f.operator
          )
        )
      ) {
        consoleLog('loadDataLinkData.did not load', dataLinkParams, {
          path,
          filters
        });
        return;
      }
    }
  }

  dispatch({
    type: DATA_LINK_ACTIONS.DATA_LINK_PARAMETERS_SET,
    payload: {
      parameters: {
        path,
        filters,
        databaseId: dataLinkDb._databaseId.database
      },
      dataLinkId: dataLink.id
    }
  });

  let countQuery;

  if (queryParts.length > 0) {
    q = query(
      collection(dataLinkDb, path),
      ...queryParts,
      limit(dataLink.pageSize || 1000)
    );
    countQuery = query(collection(dataLinkDb, path), ...queryParts);
  } else {
    q = query(collection(dataLinkDb, path), limit(dataLink.pageSize || 1000));
    countQuery = query(collection(dataLinkDb, path));
  }

  // load the records for the datasource
  if (
    dataLink.type === 'user' &&
    (firebaseUser?.uid || user?.uid || user?.userID)
  ) {
    // security rules prevent access to anything but getting our own user record
    return onSnapshot(
      doc(dataLinkDb, 'users', firebaseUser?.uid || user?.uid || user?.userID),
      async doc => {
        const userDoc = {
          id: firebaseUser?.uid || user?.uid || user?.userID,
          ...doc.data()
        };
        dispatch(
          dataLinkDocumentsLoadedAction({
            dataLinkId: dataLink.id,
            documents: [userDoc]
          })
        );

        // Need to select the user so we can work with them
        dispatch(
          dataLinkDocumentSelectedAction({
            dataLinkId: dataLink.id,
            documentId: firebaseUser.uid || user.uid || user.userID
          })
        );

        if (dataLink.countDocuments) {
          const pageCountSnap = await getCountFromServer(countQuery);
          const count = pageCountSnap.data().count;
          dispatch(
            dataLinkDocumentsCountedAction({ dataLinkId: dataLink.id, count })
          );
        }
      }
    );
  } else if (dataLink.singleDocument) {
    // security rules may prevent access to anything but getting a particular record
    // so assume that the one filter we have is the id of the document
    return onSnapshot(doc(dataLinkDb, path, filters[0].value), async doc => {
      const dataDoc = {
        id: doc.id,
        ...doc.data()
      };
      dispatch(
        dataLinkDocumentsLoadedAction({
          dataLinkId: dataLink.id,
          documents: [dataDoc]
        })
      );

      // Select the only document from this datalink so we can work with it
      dispatch(
        dataLinkDocumentSelectedAction({
          dataLinkId: dataLink.id,
          documentId: dataDoc.id
        })
      );

      if (dataLink.countDocuments) {
        const pageCountSnap = await getCountFromServer(countQuery);
        const count = pageCountSnap.data().count;
        dispatch(
          dataLinkDocumentsCountedAction({ dataLinkId: dataLink.id, count })
        );
      }
    });
  } else {
    return onSnapshot(
      query(q),
      async querySnapshot => {
        let items = [];
        querySnapshot.forEach(doc => {
          items.push({
            id: doc.id,
            ...doc.data()
          });
        });

        items = items.filter(d => !d.deleted);

        if (dataLink.sortField) {
          items = items.sort((a, b) =>
            dataLink.sortDirection === 'desc'
              ? a[dataLink.sortField] < b[dataLink.sortField]
                ? 1
                : -1
              : a[dataLink.sortField] > b[dataLink.sortField]
              ? 1
              : -1
          );
        }

        dispatch(
          dataLinkDocumentsLoadedAction({
            dataLinkId: dataLink.id,
            documents: items
          })
        );

        if (dataLink.countDocuments) {
          const pageCountSnap = await getCountFromServer(countQuery);
          const count = pageCountSnap.data().count;
          dispatch(
            dataLinkDocumentsCountedAction({ dataLinkId: dataLink.id, count })
          );
        }

        if (dataLink.singleDocument && items.length > 0) {
          dispatch(
            dataLinkDocumentSelectedAction({
              dataLinkId: dataLink.id,
              documentId: items[0].id
            })
          );
        } else if (
          autoSelection.autoSelectDocument &&
          autoSelection.autoSelectFirstDocument
        ) {
          if (items?.length > 0) {
            dispatch(
              dataLinkDocumentSelectedAction({
                dataLinkId: dataLink.id,
                documentId: items[0].id
              })
            );
          }
        } else if (
          autoSelection.autoSelectDocument &&
          autoSelection.autoSelectDocumentParameterNames &&
          autoSelection.autoSelectDocumentFieldNames
        ) {
          const paramNames = autoSelection.autoSelectDocumentParameterNames.split(
            ','
          );
          const fieldNames = autoSelection.autoSelectDocumentFieldNames.split(
            ','
          );

          if (
            !(
              paramNames?.length === 0 ||
              fieldNames.length === 0 ||
              fieldNames.length !== paramNames.length
            )
          ) {
            let matchedDocs = items;
            paramNames.forEach((paramName, index) => {
              matchedDocs = matchedDocs.filter(
                d => d[fieldNames[index]] === autoSelection.params[paramName]
              );
            });

            if (matchedDocs.length === 1) {
              dispatch(
                dataLinkDocumentSelectedAction({
                  dataLinkId: dataLink.id,
                  documentId: matchedDocs[0].id
                })
              );
            }
          }
        }
      },
      error =>
        console.warn(
          dataLink?.id,
          dataLink?.name,
          '/hooks/datalinks/utils/loadDataLinkData.js snapshot error',
          dataLinkDb._databaseId.database,
          path,
          error
        )
    );
  }
};

export {
  getDataLinkPath,
  getDataLinkFilters,
  loadDataLinkData,
  dataLinkHasDependencies
};
